"use client";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePathname, useRouter } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";
import { usersService } from "@/lib/api";
import { useAuth } from "@/lib/contexts/auth-context";

export default function LanguageSwitcher() {
	const currentLocale = useLocale();
	const router = useRouter();
	const pathname = usePathname();
	const t = useTranslations("Common");
	const { backendUser, refreshUserData } = useAuth();
	const [isUpdating, setIsUpdating] = useState(false);

	const localeLabels: Record<string, string> = {
		en: "🇺🇸 English",
		fr: "🇫🇷 Français",
	};

	const localeFlags: Record<string, string> = {
		en: "🇺🇸",
		fr: "🇫🇷",
	};
	const currentFlag = localeFlags[currentLocale as string] ?? "🏳️";

	async function handleChange(locale: string) {
		if (locale === currentLocale) return;
		if (isUpdating) return; // Prevent multiple concurrent updates

		setIsUpdating(true);

		try {
			// Update user's preferred language in the backend if user is logged in
			if (backendUser?.id) {
				await usersService.updateUser({
					id: backendUser.id,
					createUserRequest: {
						name: backendUser.name,
						email: backendUser.email,
						photoURL: backendUser.photoURL,
						preferredLanguage: locale,
					},
				});

				// Refresh user data to get the updated preferences
				await refreshUserData();
			}

			// Switch the current route to the selected locale. This also updates the NEXT_LOCALE cookie.
			router.replace(pathname, { locale });
		} catch (error) {
			console.error("Failed to update user language preference:", error);
			// Still navigate to the new locale even if the API call fails
			router.replace(pathname, { locale });
		} finally {
			setIsUpdating(false);
		}
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="outline"
					size="icon"
					aria-label={t("toggleLanguage")}
					disabled={isUpdating}
				>
					<span aria-hidden className="text-base leading-none">
						{currentFlag}
					</span>
					<span className="sr-only">{t("toggleLanguage")}</span>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				{routing.locales.map((locale) => (
					<DropdownMenuItem
						key={locale}
						onClick={() => handleChange(locale)}
						className={locale === currentLocale ? "font-medium" : undefined}
						disabled={isUpdating}
					>
						{localeLabels[locale as string] ?? (locale as string).toUpperCase()}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
